import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';

const SUPPORT_CHANNELS = [
  {
    code: 'CH',
    title: 'Live Chat',
    desc: 'Quick answers for account, portal, billing, and active project questions.',
    availability: 'Fastest response',
    action: 'Start Chat',
  },
  {
    code: 'TK',
    title: 'Support Ticket',
    desc: 'Best for bugs, feature requests, change requests, and technical investigation.',
    availability: 'Tracked workflow',
    action: 'Create Ticket',
  },
  {
    code: 'EM',
    title: 'Email Support',
    desc: 'Send screenshots, access details, scope notes, or longer technical context.',
    availability: 'Within 24 hours',
    action: 'Email Team',
  },
  {
    code: 'CR',
    title: 'Critical Response',
    desc: 'Escalation path for downtime, checkout failure, broken login, or security concerns.',
    availability: 'Priority handling',
    action: 'Escalate Issue',
  },
];

const SEVERITY_LEVELS = [
  {
    level: 'P1',
    title: 'Production Down',
    desc: 'Website, checkout, login, API, or core business workflow is unavailable.',
    response: 'Target: 1 hour',
    color: '#dc2626',
  },
  {
    level: 'P2',
    title: 'Major Impact',
    desc: 'A key feature is degraded, blocked, or affecting a large group of users.',
    response: 'Target: same day',
    color: '#d97706',
  },
  {
    level: 'P3',
    title: 'Standard Request',
    desc: 'Bug fix, content update, configuration change, or normal support question.',
    response: 'Target: 24 hours',
    color: '#2563eb',
  },
  {
    level: 'P4',
    title: 'Planning',
    desc: 'New feature ideas, improvement requests, roadmap discussion, or consultation.',
    response: 'Scheduled review',
    color: '#059669',
  },
];

const KNOWLEDGE_BASE = [
  {
    group: 'Project Support',
    articles: ['How to submit a change request', 'How milestone reviews work', 'How to share feedback clearly'],
  },
  {
    group: 'Hosting & Domains',
    articles: ['Domain DNS setup checklist', 'SSL certificate basics', 'Email forwarding configuration'],
  },
  {
    group: 'Security',
    articles: ['Access sharing best practices', 'Backup and restore workflow', 'What to do during suspected compromise'],
  },
  {
    group: 'Client Portal',
    articles: ['Finding invoices and payments', 'Tracking project progress', 'Opening and updating tickets'],
  },
];

const FAQS = [
  {
    q: 'What should I include in a support request?',
    a: 'Send the affected page or feature, what you expected, what happened instead, screenshots if possible, and whether the issue is blocking users.',
  },
  {
    q: 'How do you prioritize urgent problems?',
    a: 'We look at business impact first: downtime, payment failure, login failure, security risk, and user-facing production issues move to the top.',
  },
  {
    q: 'Can you support projects you did not build?',
    a: 'Yes. We usually begin with a technical audit so we can understand the stack, hosting, dependencies, and risk areas before making changes.',
  },
  {
    q: 'Do you provide ongoing maintenance plans?',
    a: 'Yes. Maintenance can include updates, monitoring, backups, security checks, bug fixes, performance reviews, and small feature improvements.',
  },
  {
    q: 'Where can I see billing and project updates?',
    a: 'Use the client portal for invoices, project progress, ticket history, and important delivery updates.',
  },
];

const SUPPORT_STATS = [
  ['24/7', 'Monitoring-ready support'],
  ['1h', 'Critical issue target'],
  ['24h', 'Standard response target'],
  ['4', 'Escalation levels'],
];

export default function SupportPage() {
  const [expandedFaq, setExpandedFaq] = useState(0);
  const [search, setSearch] = useState('');

  const filteredKb = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return KNOWLEDGE_BASE;

    return KNOWLEDGE_BASE.map((section) => ({
      ...section,
      articles: section.articles.filter((article) => `${section.group} ${article}`.toLowerCase().includes(query)),
    })).filter((section) => section.articles.length > 0);
  }, [search]);

  return (
    <>
      <SEOHelmet
        title="Support Center | AxentraLab"
        description="Get help from AxentraLab for project support, hosting, security, maintenance, tickets, and client portal questions."
        keywords="AxentraLab support, software support, support ticket, maintenance, help desk"
      />

      <div style={{ background: 'var(--bg)', minHeight: '100vh', overflowX: 'hidden' }}>
        <style>{`
          @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }

          .support-shell { max-width: 1180px; margin: 0 auto; padding: 0 5%; }
          .support-label {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            color: var(--teal);
            font-family: var(--font-m);
            font-size: 12px;
            font-weight: 800;
            letter-spacing: 1.5px;
            text-transform: uppercase;
            margin-bottom: 18px;
          }
          .support-label::before {
            content: '';
            width: 28px;
            height: 2px;
            border-radius: 99px;
            background: var(--teal);
          }
          .support-grid,
          .severity-grid,
          .kb-grid,
          .stats-grid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 16px;
          }
          .support-card,
          .severity-card,
          .kb-card,
          .faq-card {
            border: 1px solid var(--border);
            border-radius: 8px;
            background: #fff;
            transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
          }
          .support-card { padding: 26px 22px; min-height: 258px; display: flex; flex-direction: column; }
          .severity-card { padding: 24px 22px; }
          .kb-card { padding: 24px; }
          .support-card:hover,
          .severity-card:hover,
          .kb-card:hover {
            transform: translateY(-4px);
            border-color: rgba(37,99,235,0.3);
            box-shadow: 0 20px 44px -30px rgba(15,23,42,0.45);
          }
          .code-box {
            width: 44px;
            height: 44px;
            border-radius: 8px;
            display: grid;
            place-items: center;
            background: rgba(37,99,235,0.09);
            color: var(--teal);
            font-family: var(--font-m);
            font-size: 13px;
            font-weight: 900;
            margin-bottom: 20px;
          }
          .search-input {
            width: 100%;
            min-height: 48px;
            border-radius: 8px;
            border: 1px solid var(--border);
            background: #fff;
            color: var(--text);
            padding: 0 16px;
            font-size: 15px;
            outline: none;
          }
          .search-input:focus {
            border-color: rgba(37,99,235,0.55);
            box-shadow: 0 0 0 3px rgba(37,99,235,0.12);
          }
          .article-link {
            display: flex;
            justify-content: space-between;
            gap: 14px;
            padding: 13px 0;
            border-top: 1px solid var(--border);
            color: var(--text);
            font-size: 14px;
            font-weight: 650;
          }
          .faq-button {
            width: 100%;
            min-height: 72px;
            padding: 22px 24px;
            border: 0;
            background: transparent;
            color: var(--text);
            text-align: left;
            display: flex;
            justify-content: space-between;
            gap: 18px;
            align-items: center;
            font-size: 16px;
            font-weight: 800;
          }

          @media (max-width: 980px) {
            .support-grid,
            .severity-grid,
            .kb-grid,
            .stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            .support-feature { grid-template-columns: 1fr !important; }
          }
          @media (max-width: 680px) {
            .support-grid,
            .severity-grid,
            .kb-grid,
            .stats-grid { grid-template-columns: 1fr; }
            .hero-title { font-size: 42px !important; }
          }
          @media (prefers-reduced-motion: reduce) {
            *, *::before, *::after { animation: none !important; transition: none !important; }
          }
        `}</style>

        <section style={{ padding: '150px 0 76px', position: 'relative', overflow: 'hidden', background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}>
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'linear-gradient(var(--border) 1px,transparent 1px),linear-gradient(90deg,var(--border) 1px,transparent 1px)',
            backgroundSize: '64px 64px',
            opacity: 0.44,
          }} />
          <div className="support-shell" style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ maxWidth: 850, animation: 'fadeUp 0.7s ease both' }}>
              <div className="support-label">Support Center</div>
              <h1 className="hero-title" style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(48px, 7vw, 86px)', lineHeight: 0.98, color: 'var(--text)', margin: '0 0 24px', fontWeight: 900 }}>
                Reliable help for active projects, websites, and business systems.
              </h1>
              <p style={{ color: 'var(--muted)', fontSize: 18, lineHeight: 1.75, maxWidth: 680, marginBottom: 34 }}>
                Get clear support for bugs, maintenance, hosting, security, billing, portal access, and technical questions from the AxentraLab team.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link to="/contact" className="btn-primary" style={{ padding: '15px 30px' }}>
                  Submit Request -&gt;
                </Link>
                <Link to="/client-portal" className="btn-outline" style={{ padding: '15px 28px' }}>
                  Client Portal
                </Link>
              </div>
            </div>

            <div className="stats-grid" style={{ marginTop: 54 }}>
              {SUPPORT_STATS.map(([value, label]) => (
                <div key={label} style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'rgba(255,255,255,0.82)', padding: '22px 20px' }}>
                  <div style={{ fontFamily: 'var(--font-h)', fontSize: 34, fontWeight: 900, color: 'var(--text)', lineHeight: 1 }}>{value}</div>
                  <div style={{ marginTop: 8, color: 'var(--muted)', fontFamily: 'var(--font-m)', fontSize: 12 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="support-shell" style={{ padding: '76px 5% 92px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap', marginBottom: 36 }}>
            <div>
              <div className="support-label">Contact Channels</div>
              <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(30px, 4.5vw, 52px)', lineHeight: 1.05, color: 'var(--text)', margin: 0, fontWeight: 900 }}>
                Choose the right path for the issue.
              </h2>
            </div>
            <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.65, maxWidth: 360 }}>
              The more context you share, the faster we can diagnose and resolve the request.
            </p>
          </div>

          <div className="support-grid">
            {SUPPORT_CHANNELS.map((channel) => (
              <div key={channel.title} className="support-card">
                <div className="code-box">{channel.code}</div>
                <h3 style={{ fontFamily: 'var(--font-h)', color: 'var(--text)', fontSize: 21, fontWeight: 900, margin: '0 0 12px' }}>{channel.title}</h3>
                <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.7, marginBottom: 18 }}>{channel.desc}</p>
                <div style={{ marginTop: 'auto', paddingTop: 18, borderTop: '1px solid var(--border)' }}>
                  <div style={{ color: 'var(--teal)', fontFamily: 'var(--font-m)', fontSize: 12, fontWeight: 800, marginBottom: 12 }}>{channel.availability}</div>
                  <Link to="/contact" style={{ color: 'var(--text)', fontSize: 13, fontWeight: 800 }}>
                    {channel.action} -&gt;
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section style={{ padding: '82px 0', background: '#fff', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
          <div className="support-shell">
            <div style={{ textAlign: 'center', marginBottom: 42 }}>
              <div className="support-label" style={{ justifyContent: 'center' }}>Priority Levels</div>
              <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(30px, 4.5vw, 48px)', lineHeight: 1.08, color: 'var(--text)', margin: 0, fontWeight: 900 }}>
                Clear severity helps us move faster.
              </h2>
            </div>
            <div className="severity-grid">
              {SEVERITY_LEVELS.map((item) => (
                <div key={item.level} className="severity-card">
                  <div style={{ color: item.color, fontFamily: 'var(--font-h)', fontSize: 30, fontWeight: 900, marginBottom: 14 }}>{item.level}</div>
                  <h3 style={{ fontFamily: 'var(--font-h)', color: 'var(--text)', fontSize: 19, fontWeight: 850, margin: '0 0 10px' }}>{item.title}</h3>
                  <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.7, marginBottom: 18 }}>{item.desc}</p>
                  <div style={{ color: item.color, fontFamily: 'var(--font-m)', fontSize: 12, fontWeight: 800 }}>{item.response}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="support-shell" style={{ padding: '82px 5% 94px' }}>
          <div className="support-feature" style={{ display: 'grid', gridTemplateColumns: '0.85fr 1.15fr', gap: 22, alignItems: 'start' }}>
            <div>
              <div className="support-label">Knowledge Base</div>
              <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(30px, 4.5vw, 48px)', lineHeight: 1.08, color: 'var(--text)', margin: '0 0 18px', fontWeight: 900 }}>
                Find the right answer before opening a ticket.
              </h2>
              <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.75, marginBottom: 22 }}>
                Search support topics for project delivery, hosting, security, portal access, billing, and maintenance workflows.
              </p>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="search-input"
                placeholder="Search support topics"
              />
            </div>
            <div className="kb-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              {filteredKb.length > 0 ? filteredKb.map((section) => (
                <div key={section.group} className="kb-card">
                  <h3 style={{ fontFamily: 'var(--font-h)', color: 'var(--text)', fontSize: 20, fontWeight: 900, margin: '0 0 12px' }}>{section.group}</h3>
                  {section.articles.map((article) => (
                    <a key={article} href="#support-faq" className="article-link">
                      <span>{article}</span>
                      <span style={{ color: 'var(--teal)' }}>-&gt;</span>
                    </a>
                  ))}
                </div>
              )) : (
                <div className="kb-card" style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
                  <h3 style={{ fontFamily: 'var(--font-h)', color: 'var(--text)', fontSize: 22, marginBottom: 8 }}>No matching articles</h3>
                  <p style={{ color: 'var(--muted)', marginBottom: 18 }}>Try another keyword or send us a support request.</p>
                  <button type="button" className="btn-outline" onClick={() => setSearch('')}>Clear Search</button>
                </div>
              )}
            </div>
          </div>
        </section>

        <section id="support-faq" style={{ padding: '82px 0', background: '#fff', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
          <div className="support-shell" style={{ maxWidth: 900 }}>
            <div style={{ textAlign: 'center', marginBottom: 42 }}>
              <div className="support-label" style={{ justifyContent: 'center' }}>FAQ</div>
              <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(30px, 4.5vw, 48px)', color: 'var(--text)', margin: 0, fontWeight: 900 }}>
                Common support questions.
              </h2>
            </div>

            <div style={{ display: 'grid', gap: 14 }}>
              {FAQS.map((faq, index) => (
                <div key={faq.q} className="faq-card" style={{ overflow: 'hidden' }}>
                  <button type="button" className="faq-button" onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}>
                    <span>{faq.q}</span>
                    <span style={{ color: 'var(--teal)', fontSize: 24, lineHeight: 1 }}>{expandedFaq === index ? '-' : '+'}</span>
                  </button>
                  {expandedFaq === index && (
                    <div style={{ padding: '0 24px 24px', color: 'var(--muted)', fontSize: 15, lineHeight: 1.75 }}>
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="support-shell" style={{ padding: '94px 5% 110px' }}>
          <div style={{
            border: '1px solid var(--border)',
            borderRadius: 8,
            background: '#0f172a',
            color: '#fff',
            padding: 'clamp(42px, 7vw, 76px)',
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: 28,
            alignItems: 'center',
          }} className="support-feature">
            <div>
              <div style={{ color: '#93c5fd', fontFamily: 'var(--font-m)', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', marginBottom: 16 }}>Need help now?</div>
              <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(32px, 5vw, 58px)', lineHeight: 1.02, margin: '0 0 18px', fontWeight: 900 }}>
                Tell us what is broken, blocked, or ready to improve.
              </h2>
              <p style={{ color: '#cbd5e1', fontSize: 16, lineHeight: 1.7, maxWidth: 620 }}>
                Include links, screenshots, login context, severity, and expected behavior. We will help you choose the next practical step.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn-primary" style={{ padding: '15px 28px', background: '#fff', color: '#0f172a' }}>
                Submit Request
              </Link>
              <Link to="/services/support-and-maintenance" className="btn-outline" style={{ padding: '15px 26px', color: '#fff', borderColor: 'rgba(255,255,255,0.36)' }}>
                Maintenance Plans
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

